import React, { createContext, useContext, useCallback } from 'react';
import type { Transformable, Transformation } from '../hooks/useTransformable';
import { defaultTheme } from '../themes/defaultTheme';

export interface TransformableTheme {
  element?: React.CSSProperties;
  resizeHandlers?: {
    base?: React.CSSProperties;
    [direction: string]: React.CSSProperties | undefined;
  };
  rotationHandler?: {
    base?: React.CSSProperties;
    [direction: string]: React.CSSProperties | undefined;
  };
  origin?: React.CSSProperties;
}

interface TransformableContextType {
  // Selection state
  selected: Set<string>;
  setSelected: (ids: Set<string>) => void;
  
  // Grid configuration
  gridSize: number;
  snapToGrid: boolean;
  
  // Theme configuration
  theme: TransformableTheme;
  
  // Origin display
  showOrigin?: boolean;
  
  // Elements data
  elements: Transformable[];
  
  // Transformation callbacks
  onDragEnd: (transformation: Transformation) => void;
  onMultiDragEnd: (transformations: Transformation[]) => void;
  onMultiDragMove: (deltaX: number, deltaY: number) => void;
  onMultiResizeMove: (deltaX: number, deltaY: number, deltaWidth: number, deltaHeight: number) => void;
  onMultiResizeEnd: (transformations: Transformation[]) => void;
  onMultiRotateMove: (deltaRotation: number) => void;
  onMultiRotateEnd: (transformations: Transformation[]) => void;
  onResizeEnd: (transformation: Transformation) => void;
  onRotateEnd: (transformation: Transformation) => void;
  onSelect: (elementId: string, isMultiSelect: boolean) => void;
  
  // Utility functions
  snapValue: (value: number) => number;
}

const TransformableContext = createContext<TransformableContextType | null>(null);

interface TransformableProviderProps {
  children: React.ReactNode;
  selected: Set<string>;
  setSelected: (ids: Set<string>) => void;
  gridSize: number;
  snapToGrid: boolean;
  elements: Transformable[];
  handleUpdate: (id: string, updates: Partial<Transformable>) => void;
  theme?: TransformableTheme;
  showOrigin?: boolean;
}

export const TransformableProvider: React.FC<TransformableProviderProps> = ({
  children,
  selected,
  setSelected,
  gridSize,
  snapToGrid,
  elements,
  handleUpdate,
  theme,
  showOrigin,
}) => {
  const snapValue = useCallback((value: number) => {
    return snapToGrid ? Math.round(value / gridSize) * gridSize : value;
  }, [snapToGrid, gridSize]);

  // Merge custom theme with default theme
  const mergedTheme = useCallback(() => {
    if (!theme) return defaultTheme;
    
    return {
      element: {
        ...defaultTheme.element,
        ...theme.element,
      },
      resizeHandlers: {
        ...defaultTheme.resizeHandlers,
        ...theme.resizeHandlers,
      },
      rotationHandler: {
        ...defaultTheme.rotationHandler,
        ...theme.rotationHandler,
      },
      origin: {
        ...defaultTheme.origin,
        ...theme.origin,
      },
    };
  }, [theme])();

  const onDragEnd = useCallback((transformation: Transformation) => {
    handleUpdate(transformation.id, { x: transformation.x!, y: transformation.y! });
  }, [handleUpdate]);

  const onMultiDragEnd = useCallback((transformations: Transformation[]) => {
    transformations.forEach(transformation => {
      if (transformation.deltaX !== undefined && transformation.deltaY !== undefined) {
        const element = elements.find(el => el.id === transformation.id);
        if (element) {
          const newX = snapValue(element.x + transformation.deltaX);
          const newY = snapValue(element.y + transformation.deltaY);
          handleUpdate(transformation.id, { x: newX, y: newY });
        }
      }
    });
  }, [elements, snapValue, handleUpdate]);

  const onMultiDragMove = useCallback((deltaX: number, deltaY: number) => {
    // Update all selected elements in real-time during multi-drag
    selected.forEach(id => {
      const element = elements.find(el => el.id === id);
      if (element) {
        const newX = snapValue(element.x + deltaX);
        const newY = snapValue(element.y + deltaY);
        handleUpdate(id, { x: newX, y: newY });
      }
    });
  }, [selected, elements, snapValue, handleUpdate]);

  const onMultiResizeMove = useCallback((deltaX: number, deltaY: number, deltaWidth: number, deltaHeight: number) => {
    // Update all selected elements in real-time during multi-resize
    selected.forEach(id => {
      const element = elements.find(el => el.id === id);
      if (element) {
        const newX = snapValue(element.x + deltaX);
        const newY = snapValue(element.y + deltaY);
        const newWidth = Math.max(50, snapValue(element.width + deltaWidth));
        const newHeight = Math.max(50, snapValue(element.height + deltaHeight));
        handleUpdate(id, { x: newX, y: newY, width: newWidth, height: newHeight });
      }
    });
  }, [selected, elements, snapValue, handleUpdate]);

  const onMultiResizeEnd = useCallback((transformations: Transformation[]) => {
    transformations.forEach(transformation => {
      if (transformation.deltaX !== undefined && transformation.deltaY !== undefined && 
          transformation.deltaWidth !== undefined && transformation.deltaHeight !== undefined) {
        const element = elements.find(el => el.id === transformation.id);
        if (element) {
          const newX = snapValue(element.x + transformation.deltaX);
          const newY = snapValue(element.y + transformation.deltaY);
          const newWidth = Math.max(50, snapValue(element.width + transformation.deltaWidth));
          const newHeight = Math.max(50, snapValue(element.height + transformation.deltaHeight));
          handleUpdate(transformation.id, { x: newX, y: newY, width: newWidth, height: newHeight });
        }
      }
    });
  }, [elements, snapValue, handleUpdate]);

  const onMultiRotateMove = useCallback((deltaRotation: number) => {
    // Update all selected elements in real-time during multi-rotate
    selected.forEach(id => {
      const element = elements.find(el => el.id === id);
      if (element) {
        const newRotation = element.rotation + deltaRotation;
        handleUpdate(id, { rotation: newRotation });
      }
    });
  }, [selected, elements, handleUpdate]);

  const onMultiRotateEnd = useCallback((transformations: Transformation[]) => {
    transformations.forEach(transformation => {
      if (transformation.deltaRotation !== undefined) {
        const element = elements.find(el => el.id === transformation.id);
        if (element) {
          const newRotation = element.rotation + transformation.deltaRotation;
          handleUpdate(transformation.id, { rotation: newRotation });
        }
      }
    });
  }, [elements, handleUpdate]);

  const onResizeEnd = useCallback((transformation: Transformation) => {
    handleUpdate(transformation.id, { 
      x: transformation.x!, 
      y: transformation.y!, 
      width: transformation.width!, 
      height: transformation.height! 
    });
  }, [handleUpdate]);

  const onRotateEnd = useCallback((transformation: Transformation) => {
    handleUpdate(transformation.id, { rotation: transformation.rotation! });
  }, [handleUpdate]);

  const onSelect = useCallback((elementId: string, isMultiSelect: boolean) => {
    if (isMultiSelect) {
      const newSelection = new Set(selected);
      if (newSelection.has(elementId)) {
        newSelection.delete(elementId);
      } else {
        newSelection.add(elementId);
      }
      setSelected(newSelection);
    } else {
      if (!selected.has(elementId)) {
        setSelected(new Set([elementId]));
      }
    }
  }, [selected, setSelected]);

  const contextValue: TransformableContextType = {
    selected,
    setSelected,
    gridSize,
    snapToGrid,
    elements,
    onDragEnd,
    onMultiDragEnd,
    onMultiDragMove,
    onMultiResizeMove,
    onMultiResizeEnd,
    onMultiRotateMove,
    onMultiRotateEnd,
    onResizeEnd,
    onRotateEnd,
    onSelect,
    snapValue,
    theme: mergedTheme,
    showOrigin,
  };

  return (
    <TransformableContext.Provider value={contextValue}>
      {children}
    </TransformableContext.Provider>
  );
};

export const useTransformable = (): TransformableContextType => {
  const context = useContext(TransformableContext);
  if (!context) {
    throw new Error('useTransformable must be used within a TransformableProvider');
  }
  return context;
};
